import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  CreateFaseDto, UpdateFaseDto,
  CreateEstadoLoteDto, UpdateEstadoLoteDto
} from '@/types';

// Catalogo de fases (las 9 columnas del Kanban de tramites CFIA).
// Cambian poco, cache largo.
export function useFases() {
  return useQuery({
    queryKey: ['fases'],
    queryFn: api.fases.list,
    staleTime: 5 * 60 * 1000,
  });
}

// Catalogo de estados detallados (~30 filas, agrupados por fase).
// Soporta filtros opcionales en server para usar el mismo hook al
// poblar el dropdown del DnD (filtrando por idFase de la columna destino).
export function useEstadosLote(params: { idFase?: number; tipoProyecto?: string } = {}) {
  return useQuery({
    queryKey: ['estados-lote', params.idFase ?? null, params.tipoProyecto ?? null],
    queryFn: () => api.estadosLote.list(params),
    staleTime: 5 * 60 * 1000,
  });
}

// Cualquier mutation sobre fases o estados invalida ambas listas (porque
// se ven juntas en /configuracion) y el kanban (donde el header de las
// columnas usa el color/nombre de la fase y los estados que el dropdown
// muestra).
function bumpCatalog(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['fases'] });
  qc.invalidateQueries({ queryKey: ['estados-lote'] });
  qc.invalidateQueries({ queryKey: ['lotes-kanban'] });
}

// ============================================================
// Fases — mutations
// ============================================================
export function useCreateFase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateFaseDto) => api.fases.create(dto),
    onSuccess: () => bumpCatalog(qc),
  });
}

export function useUpdateFase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateFaseDto }) =>
      api.fases.update(id, dto),
    onSuccess: () => bumpCatalog(qc),
  });
}

export function useDeleteFase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.fases.remove(id),
    onSuccess: () => bumpCatalog(qc),
  });
}

export function useReorderFases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) => api.fases.reorder(orderedIds),
    onSuccess: () => bumpCatalog(qc),
  });
}

// ============================================================
// Estados de Lote — mutations
// ============================================================
export function useCreateEstadoLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateEstadoLoteDto) => api.estadosLote.create(dto),
    onSuccess: () => bumpCatalog(qc),
  });
}

export function useUpdateEstadoLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateEstadoLoteDto }) =>
      api.estadosLote.update(id, dto),
    onSuccess: () => bumpCatalog(qc),
  });
}

export function useDeleteEstadoLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.estadosLote.remove(id),
    onSuccess: () => bumpCatalog(qc),
  });
}

export function useReorderEstadosLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ idFase, orderedIds }: { idFase: number; orderedIds: number[] }) =>
      api.estadosLote.reorder(idFase, orderedIds),
    onSuccess: () => bumpCatalog(qc),
  });
}
